/**
 * FormulaEngine Service - HyperFormula Integration
 * Story 6.3: Integrate HyperFormula Engine
 *
 * Wraps HyperFormula with project-specific API for Excel-like formula evaluation.
 * Supports 400+ Excel functions, bracket notation [ColumnName], and reactive recalculation.
 */

import { HyperFormula, ErrorType, CellError, DetailedCellError, FunctionPlugin } from 'hyperformula';
import type { GridColumn, GridRow } from '../views/grid/types/gridTypes';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Formula error codes matching Excel conventions
 * AC-6.3.6: Error Handling
 */
export type FormulaErrorCode =
  | '#REF!'      // Invalid cell reference
  | '#VALUE!'    // Wrong value type
  | '#DIV/0!'    // Division by zero
  | '#NAME?'     // Unknown function/name
  | '#N/A'       // Value not available
  | '#NUM!'      // Numeric error
  | '#NULL!'     // Null reference
  | '#CYCLE!'    // Circular reference
  | '#ERROR!';   // General error

export interface FormulaError {
  code: FormulaErrorCode;
  message: string;
}

export interface EvaluationResult {
  value: unknown;
  error?: FormulaError;
}

/**
 * Configuration for FormulaEngine initialization
 */
export interface FormulaEngineConfig {
  sheetId: string;
  columns: GridColumn[];
  rows: GridRow[];
}

// =============================================================================
// CHILDREN CUSTOM FUNCTION PLUGIN
// AC-6.3.10: Backward Compatibility - Preserve CHILDREN reference
// =============================================================================

/**
 * Custom context passed to CHILDREN function for hierarchical aggregation.
 * This is set before each evaluation that needs CHILDREN support.
 */
let childrenContext: {
  allRows: GridRow[];
  currentRowId: string;
  targetColumnIndex: number;
} | null = null;

export function setChildrenContext(ctx: typeof childrenContext): void {
  childrenContext = ctx;
}

export function clearChildrenContext(): void {
  childrenContext = null;
}

/**
 * CHILDREN function plugin for HyperFormula
 * Returns an array of values from child rows for the current column
 * AC-6.3.10: Backward Compatibility - CHILDREN function for hierarchical rows
 */
class ChildrenPlugin extends FunctionPlugin {
  public static implementedFunctions = {
    CHILDREN: {
      method: 'children',
      parameters: [],
    },
  };

  public children(): number[] {
    if (!childrenContext) {
      return [];
    }

    const { allRows, currentRowId, targetColumnIndex } = childrenContext;
    const childRows = allRows.filter((r) => r.parentId === currentRowId);

    return childRows.map((row) => {
      const colValues = Object.values(row.values);
      const val = colValues[targetColumnIndex];
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        const n = parseFloat(val);
        return isNaN(n) ? 0 : n;
      }
      return 0;
    });
  }
}

// Register the custom plugin with HyperFormula
// eslint-disable-next-line @typescript-eslint/no-explicit-any
HyperFormula.registerFunctionPlugin(ChildrenPlugin as any, ChildrenPlugin.implementedFunctions as any);

// =============================================================================
// FORMULA ENGINE MANAGER
// AC-6.3.2: FormulaEngine Service Creation
// AC-6.3.7: Sheet Instance Management
// =============================================================================

export class FormulaEngineManager {
  // =============================================================================
  // UNIFIED SHARED ENGINE
  // AC-6.6.3: Single HyperFormula instance for all sheets (cross-sheet support)
  // Story 6.6 AI Review: Fixed split-brain by using ONLY shared engine
  // =============================================================================

  /** Single shared HyperFormula engine for ALL sheets (unified architecture) */
  private sharedEngine: HyperFormula | null = null;

  /** Maps sheet ID to HyperFormula numeric sheet ID */
  private sheetNumericIds = new Map<string, number>();

  /** Maps sheet ID to column mappings (colId -> index) */
  private columnMaps = new Map<string, Map<string, number>>();

  /** Maps sheet ID to column name mappings (uppercase name -> colId) */
  private columnNameMaps = new Map<string, Map<string, string>>();

  // =============================================================================
  // CROSS-SHEET REFERENCE LISTENERS
  // AC-6.6 AI Review: Event emission for cross-sheet reactivity
  // =============================================================================

  /** Listeners for sheet data changes (cross-sheet reactivity) */
  private changeListeners = new Set<(sheetId: string) => void>();

  /**
   * Subscribe to sheet data changes for cross-sheet reactivity
   * @param listener - Callback when any sheet data changes
   * @returns Unsubscribe function
   */
  public onSheetChange(listener: (sheetId: string) => void): () => void {
    this.changeListeners.add(listener);
    return () => this.changeListeners.delete(listener);
  }

  /**
   * Emit sheet change event for cross-sheet reactivity
   */
  private emitSheetChange(sheetId: string): void {
    this.changeListeners.forEach(listener => listener(sheetId));
  }

  /**
   * Get the shared HyperFormula engine (unified for all sheets)
   * AC-6.3.2: GPL-v3 license configuration
   * AC-6.6.3: Single engine enables native cross-sheet references
   */
  public getEngineForSheet(_sheetId: string): HyperFormula {
    return this.getSharedEngine();
  }

  /**
   * Initialize a sheet with columns and row data
   * AC-6.3.7: Sheet instance management with column definitions and row data sync
   */
  public initializeSheet(sheetId: string, columns: GridColumn[], rows: GridRow[]): void {
    const engine = this.getEngineForSheet(sheetId);

    // Remove existing sheet if present, then add fresh
    const existingNumericId = this.sheetNumericIds.get(sheetId);
    if (existingNumericId !== undefined) {
      engine.removeSheet(existingNumericId);
      this.sheetNumericIds.delete(sheetId);
    }

    // Add the sheet - addSheet returns sheet name, use getSheetId for numeric ID
    engine.addSheet(sheetId);
    const hfNumericId = engine.getSheetId(sheetId);
    if (hfNumericId === undefined) {
      throw new Error(`Failed to create sheet: ${sheetId}`);
    }
    this.sheetNumericIds.set(sheetId, hfNumericId);

    // Build column index mapping
    const colMap = new Map<string, number>();
    const colNameMap = new Map<string, string>();
    columns.forEach((col, idx) => {
      colMap.set(col.id, idx);
      colNameMap.set(col.label.toUpperCase(), col.id);
    });
    this.columnMaps.set(sheetId, colMap);
    this.columnNameMaps.set(sheetId, colNameMap);

    // Populate sheet with row data
    if (rows.length > 0 && columns.length > 0) {
      const data = rows.map((row) =>
        columns.map((col) => {
          const val = row.values[col.id];
          // Convert to appropriate type for HyperFormula
          if (val === null || val === undefined) return null;
          if (typeof val === 'number') return val;
          if (typeof val === 'boolean') return val;
          if (typeof val === 'string') {
            // Check if it's a formula
            if (val.startsWith('=')) return val;
            // Try to parse as number
            const n = parseFloat(val);
            if (!isNaN(n) && isFinite(n) && String(n) === val.trim()) return n;
            return val;
          }
          return String(val);
        })
      );
      engine.setCellContents({ sheet: hfNumericId, row: 0, col: 0 }, data);
    }
  }

  /**
   * Sync a single cell change to HyperFormula
   * AC-6.3.8: Reactive recalculation
   */
  public setCellValue(
    sheetId: string,
    rowIndex: number,
    columnId: string,
    value: unknown
  ): void {
    const engine = this.getEngineForSheet(sheetId);
    const hfNumericId = this.sheetNumericIds.get(sheetId);
    if (hfNumericId === undefined) return;

    const colMap = this.columnMaps.get(sheetId);
    if (!colMap) return;

    const colIndex = colMap.get(columnId);
    if (colIndex === undefined) return;

    // Cast value to RawCellContent compatible type
    const cellValue = value === null || value === undefined ? null
      : typeof value === 'number' || typeof value === 'boolean' || typeof value === 'string' ? value
      : String(value);
    engine.setCellContents({ sheet: hfNumericId, row: rowIndex, col: colIndex }, [[cellValue]]);
    // Emit change for cross-sheet reactivity
    this.emitSheetChange(sheetId);
  }

  /**
   * Add a row to the HyperFormula sheet
   */
  public addRow(sheetId: string, columns: GridColumn[], row: GridRow): void {
    const engine = this.getEngineForSheet(sheetId);
    const hfNumericId = this.sheetNumericIds.get(sheetId);
    if (hfNumericId === undefined) return;

    const rowData = columns.map((col) => {
      const val = row.values[col.id];
      if (val === null || val === undefined) return null;
      if (typeof val === 'number') return val;
      if (typeof val === 'string' && val.startsWith('=')) return val;
      return val;
    });

    // Get current dimensions and add at end
    const dims = engine.getSheetDimensions(hfNumericId);
    engine.setCellContents({ sheet: hfNumericId, row: dims.height, col: 0 }, [rowData]);
  }

  /**
   * Update a row in the HyperFormula sheet
   */
  public updateRow(
    sheetId: string,
    rowIndex: number,
    columns: GridColumn[],
    row: GridRow
  ): void {
    const engine = this.getEngineForSheet(sheetId);
    const hfNumericId = this.sheetNumericIds.get(sheetId);
    if (hfNumericId === undefined) return;

    const rowData = columns.map((col) => {
      const val = row.values[col.id];
      if (val === null || val === undefined) return null;
      if (typeof val === 'number') return val;
      if (typeof val === 'string' && val.startsWith('=')) return val;
      return val;
    });

    engine.setCellContents({ sheet: hfNumericId, row: rowIndex, col: 0 }, [rowData]);
  }

  /**
   * Remove a row from the HyperFormula sheet
   */
  public removeRow(sheetId: string, rowIndex: number): void {
    const engine = this.getEngineForSheet(sheetId);
    const hfNumericId = this.sheetNumericIds.get(sheetId);
    if (hfNumericId === undefined) return;

    engine.removeRows(hfNumericId, [rowIndex, 1]);
  }

  /**
   * Convert bracket notation [ColumnName] to Excel-style cell reference
   * AC-6.3.4: Bracket Notation Support
   */
  public convertBracketNotation(
    sheetId: string,
    formula: string,
    rowIndex: number
  ): string {
    const colNameMap = this.columnNameMaps.get(sheetId);
    const colMap = this.columnMaps.get(sheetId);
    if (!colNameMap || !colMap) return formula;

    // Functions that should convert single [Column] arguments to column ranges
    const rangeFunctions = new Set(['SUM', 'AVERAGE', 'MIN', 'MAX', 'COUNT', 'COUNTA', 'COUNTBLANK', 'PRODUCT']);

    // Check if this formula uses a single bracket argument in a range function
    const singleBracketPattern = /^=([A-Z]+)\(\s*\[([^\]]+)\]\s*\)$/i;
    const singleBracketMatch = formula.match(singleBracketPattern);
    if (singleBracketMatch) {
      const funcName = singleBracketMatch[1].toUpperCase();
      const colName = singleBracketMatch[2];
      if (rangeFunctions.has(funcName)) {
        const colId = colNameMap.get(colName.toUpperCase());
        if (colId) {
          const colIndex = colMap.get(colId);
          if (colIndex !== undefined) {
            const colLetter = this.indexToColumnLetter(colIndex);
            return `=${funcName}(${colLetter}:${colLetter})`;
          }
        }
      }
    }

    // Match [ColumnName] pattern (case-insensitive) but NOT [SheetName]![ColumnName] patterns
    // Use negative lookbehind to exclude matches that are part of cross-sheet references
    // First, we handle only standalone [ColumnName] patterns that are not preceded by ]!
    return formula.replace(/\[([^\]]+)\](?!!)/gi, (match, colName, offset) => {
      // Check if this is part of a cross-sheet reference (preceded by ]!)
      // Look backwards for ]! pattern
      const textBefore = formula.substring(0, offset);
      const isPartOfCrossSheet = textBefore.match(/\[[^\]]+\]!$/);
      if (isPartOfCrossSheet) {
        return match; // Don't convert - this is the column part of a cross-sheet reference
      }

      const colId = colNameMap.get(colName.toUpperCase());
      if (!colId) return match; // Keep original if column not found

      const colIndex = colMap.get(colId);
      if (colIndex === undefined) return match;

      // Convert to Excel column letter (A, B, C, ... AA, AB, etc.)
      const colLetter = this.indexToColumnLetter(colIndex);
      // Use 1-based row number
      return `${colLetter}${rowIndex + 1}`;
    });
  }

  // =============================================================================
  // CROSS-SHEET REFERENCE SUPPORT
  // AC-6.6.2, AC-6.6.3: Cross-Sheet Reference Syntax and Evaluation
  // Story 6.6: Implement Cross-Sheet References
  // =============================================================================

  /**
   * Regex pattern to match cross-sheet reference syntax: [SheetName]![ColumnName]
   * Captures: group 1 = sheet name, group 2 = column name
   */
  private static readonly CROSS_SHEET_PATTERN = /\[([^\]]+)\]!\[([^\]]+)\]/gi;

  /**
   * Regex pattern to detect any cross-sheet reference (for validation)
   * Matches [SheetName]! pattern
   */
  private static readonly CROSS_SHEET_DETECT_PATTERN = /\[([^\]]+)\]!/g;

  /**
   * Check if a formula contains cross-sheet references
   * AC-6.6.2: Detect [SheetName]![ColumnName] syntax
   *
   * @param formula - The formula string to check
   * @returns true if formula contains cross-sheet references
   */
  public hasCrossSheetReference(formula: string): boolean {
    // Reset lastIndex to ensure clean match
    FormulaEngineManager.CROSS_SHEET_DETECT_PATTERN.lastIndex = 0;
    return FormulaEngineManager.CROSS_SHEET_DETECT_PATTERN.test(formula);
  }

  /**
   * Extract all unique sheet names referenced in a formula
   * AC-6.6.2: Parse cross-sheet reference syntax
   *
   * @param formula - The formula string to parse
   * @returns Array of unique sheet names referenced
   */
  public extractReferencedSheets(formula: string): string[] {
    const sheets = new Set<string>();
    // Reset lastIndex before using exec
    FormulaEngineManager.CROSS_SHEET_DETECT_PATTERN.lastIndex = 0;

    let match: RegExpExecArray | null;
    while ((match = FormulaEngineManager.CROSS_SHEET_DETECT_PATTERN.exec(formula)) !== null) {
      sheets.add(match[1]); // match[1] is the sheet name
    }

    return Array.from(sheets);
  }

  /**
   * Convert cross-sheet bracket notation to HyperFormula native format
   * AC-6.6.2: Convert [SheetName]![ColumnName] to SheetName!A1 format
   *
   * Pattern: [SheetName]![ColumnName] -> SheetName!A1 or 'Sheet Name'!A1
   *
   * @param formula - The formula with bracket notation
   * @param currentSheetId - ID of the current sheet (for resolving local references)
   * @param rowIndex - Row index for cell reference (0-based)
   * @param sheetNameToId - Mapping of display names to sheet IDs
   * @returns Formula with HyperFormula native syntax
   */
  public convertCrossSheetNotation(
    formula: string,
    _currentSheetId: string,
    rowIndex: number,
    sheetNameToId: Record<string, string>
  ): string {
    // Reset lastIndex before replace
    FormulaEngineManager.CROSS_SHEET_PATTERN.lastIndex = 0;

    return formula.replace(
      FormulaEngineManager.CROSS_SHEET_PATTERN,
      (_match, sheetName: string, colName: string) => {
        // Look up the sheet ID from the name mapping
        const targetSheetId = sheetNameToId[sheetName];
        if (!targetSheetId) {
          // Sheet not found - return #REF! error marker
          return '#REF!';
        }

        // Get the column mappings for the target sheet
        const colNameMap = this.columnNameMaps.get(targetSheetId);
        const colMap = this.columnMaps.get(targetSheetId);

        if (!colNameMap || !colMap) {
          return '#REF!'; // Sheet not initialized
        }

        // Find the column ID from the name (case-insensitive)
        const colId = colNameMap.get(colName.toUpperCase());
        if (!colId) {
          return '#REF!'; // Column not found
        }

        // Get the column index
        const colIndex = colMap.get(colId);
        if (colIndex === undefined) {
          return '#REF!';
        }

        // Convert to Excel column letter
        const colLetter = this.indexToColumnLetter(colIndex);

        // Quote sheet name if it contains spaces or special characters
        const quotedSheetName = sheetName.includes(' ') || sheetName.includes("'")
          ? `'${sheetName.replace(/'/g, "''")}'`
          : sheetName;

        // Return HyperFormula native format: SheetName!A1
        return `${quotedSheetName}!${colLetter}${rowIndex + 1}`;
      }
    );
  }

  // =============================================================================
  // SHARED ENGINE METHODS
  // AC-6.6.3: Cross-Sheet Formula Evaluation
  // AC-6.6.4: Circular Reference Detection
  // AC-6.6.7: Sheet Rename Handling
  // Story 6.6: Implement Cross-Sheet References
  // =============================================================================

  /**
   * Get or create the shared HyperFormula engine
   * AC-6.6.3: Single engine for all sheets enables native cross-sheet references
   */
  public getSharedEngine(): HyperFormula {
    if (!this.sharedEngine) {
      this.sharedEngine = HyperFormula.buildEmpty({
        licenseKey: 'gpl-v3',
      });
    }
    return this.sharedEngine;
  }

  /**
   * Initialize a sheet in the shared engine (alias for initializeSheet)
   * AC-6.6.3: Register sheet by display name for HyperFormula native references
   * @deprecated Use initializeSheet instead - unified architecture
   */
  public initializeSheetInSharedEngine(
    sheetName: string,
    columns: GridColumn[],
    rows: GridRow[]
  ): void {
    this.initializeSheet(sheetName, columns, rows);
  }

  /**
   * Get sheet numeric ID by display name
   * AC-6.6.3: Lookup sheet in shared engine
   */
  public getSheetIdByName(sheetName: string): number | undefined {
    return this.sheetNumericIds.get(sheetName);
  }

  /**
   * Evaluate a formula in the shared engine (for cross-sheet references)
   * AC-6.6.3: Cross-sheet formula evaluation using HyperFormula native syntax
   *
   * @param sheetName - Display name of the current sheet
   * @param formula - Formula in HyperFormula native format (e.g., =SheetName!A1)
   * @param rowIndex - Row index for context (not used directly, for future expansion)
   */
  public evaluateCrossSheetFormula(
    sheetName: string,
    formula: string,
    _rowIndex: number
  ): EvaluationResult {
    const engine = this.getSharedEngine();
    const sheetId = this.sheetNumericIds.get(sheetName);

    if (sheetId === undefined) {
      return {
        value: null,
        error: { code: '#REF!', message: `Sheet not found: ${sheetName}` },
      };
    }

    try {
      let processedFormula = formula;
      if (!formula.startsWith('=')) {
        processedFormula = '=' + formula;
      }

      const result = engine.calculateFormula(processedFormula, sheetId);

      if (result instanceof DetailedCellError || result instanceof CellError) {
        return {
          value: null,
          error: this.mapHyperFormulaError(result),
        };
      }

      return { value: result };
    } catch (e) {
      return {
        value: null,
        error: { code: '#ERROR!', message: String(e) },
      };
    }
  }

  /**
   * Set a cell value in the shared engine (alias for setCellValue)
   * AC-6.6.3: Update shared engine data for reactive cross-sheet recalculation
   * @deprecated Use setCellValue instead - unified architecture
   */
  public setCellValueInSharedEngine(
    sheetName: string,
    rowIndex: number,
    columnId: string,
    value: unknown
  ): void {
    this.setCellValue(sheetName, rowIndex, columnId, value);
    // Emit change event for cross-sheet reactivity
    this.emitSheetChange(sheetName);
  }

  /**
   * Handle sheet rename in the shared engine
   * AC-6.6.7: Update formula text and engine registration when sheet is renamed
   * Returns map of affected formulas for persistence layer to update
   *
   * @param oldName - Previous sheet name
   * @param newName - New sheet name
   * @returns Map of sheetId -> updated formula strings (for persistence)
   */
  public handleSheetRename(oldName: string, newName: string): Map<string, string[]> {
    const engine = this.getSharedEngine();
    const sheetId = this.sheetNumericIds.get(oldName);
    const affectedFormulas = new Map<string, string[]>();

    if (sheetId === undefined) {
      console.warn(`Cannot rename sheet: "${oldName}" not found in shared engine`);
      return affectedFormulas;
    }

    // HyperFormula's renameSheet automatically updates all formula references
    engine.renameSheet(sheetId, newName);

    // Update our internal mappings
    this.sheetNumericIds.delete(oldName);
    this.sheetNumericIds.set(newName, sheetId);

    // Move column mappings to new name
    const colMap = this.columnMaps.get(oldName);
    const colNameMap = this.columnNameMaps.get(oldName);
    if (colMap) {
      this.columnMaps.delete(oldName);
      this.columnMaps.set(newName, colMap);
    }
    if (colNameMap) {
      this.columnNameMaps.delete(oldName);
      this.columnNameMaps.set(newName, colNameMap);
    }

    // Emit change event for all sheets (formulas may have been updated)
    this.sheetNumericIds.forEach((_, sheetName) => {
      this.emitSheetChange(sheetName);
    });

    return affectedFormulas;
  }

  /**
   * Destroy the shared engine and all registrations
   * Call this when the app unmounts or resets
   */
  public destroySharedEngine(): void {
    if (this.sharedEngine) {
      this.sharedEngine.destroy();
      this.sharedEngine = null;
      this.sheetNumericIds.clear();
      this.columnMaps.clear();
      this.columnNameMaps.clear();
      this.changeListeners.clear();
    }
  }

  /**
   * Evaluate a formula for a specific cell
   * AC-6.3.3: Excel Function Support
   * AC-6.3.5: Cell Notation Support
   */
  public evaluateFormula(
    sheetId: string,
    formula: string,
    rowIndex: number,
    columnId: string,
    allRows?: GridRow[]
  ): EvaluationResult {
    const engine = this.getEngineForSheet(sheetId);
    const hfNumericId = this.sheetNumericIds.get(sheetId);
    if (hfNumericId === undefined) {
      return { value: null, error: { code: '#REF!', message: 'Sheet not found' } };
    }

    // Set up CHILDREN context if needed
    const colMap = this.columnMaps.get(sheetId);
    const targetColIndex = colMap?.get(columnId) ?? 0;
    if (allRows && formula.toUpperCase().includes('CHILDREN')) {
      setChildrenContext({
        allRows,
        currentRowId: allRows[rowIndex]?.id ?? '',
        targetColumnIndex: targetColIndex,
      });
    }

    try {
      // Convert bracket notation to cell references
      let processedFormula = formula;
      if (!formula.startsWith('=')) {
        processedFormula = '=' + formula;
      }
      processedFormula = this.convertBracketNotation(sheetId, processedFormula, rowIndex);

      // Handle cross-sheet references if present
      if (this.hasCrossSheetReference(processedFormula)) {
        // Build sheet name to ID mapping (display name to display name since sheetId is display name)
        const sheetNameToId: Record<string, string> = {};
        for (const name of this.sheetNumericIds.keys()) {
          sheetNameToId[name] = name;
        }
        processedFormula = this.convertCrossSheetNotation(processedFormula, sheetId, rowIndex, sheetNameToId);
      }

      // Evaluate using HyperFormula
      const result = engine.calculateFormula(processedFormula, hfNumericId);

      // Map HyperFormula errors to our error codes
      // calculateFormula returns DetailedCellError, getCellValue returns CellError
      if (result instanceof DetailedCellError || result instanceof CellError) {
        return {
          value: null,
          error: this.mapHyperFormulaError(result),
        };
      }

      return { value: result };
    } catch (e) {
      return {
        value: null,
        error: { code: '#ERROR!', message: String(e) },
      };
    } finally {
      clearChildrenContext();
    }
  }

  /**
   * Get the computed value for a cell
   * AC-6.3.8: Reactive recalculation - get current computed values
   */
  public getCellValue(sheetId: string, rowIndex: number, columnId: string): unknown {
    const engine = this.getEngineForSheet(sheetId);
    const hfNumericId = this.sheetNumericIds.get(sheetId);
    if (hfNumericId === undefined) return null;

    const colMap = this.columnMaps.get(sheetId);
    if (!colMap) return null;

    const colIndex = colMap.get(columnId);
    if (colIndex === undefined) return null;

    const result = engine.getCellValue({ sheet: hfNumericId, row: rowIndex, col: colIndex });

    // Check if result is a CellError using type guard
    if (result !== null && typeof result === 'object' && 'type' in result) {
      return this.mapHyperFormulaError(result as unknown as CellError).code;
    }

    return result;
  }

  /**
   * Update column mapping when columns are renamed
   * AC-6.3.4: Column renames automatically update formulas
   */
  public updateColumnMapping(sheetId: string, columns: GridColumn[]): void {
    const colMap = new Map<string, number>();
    const colNameMap = new Map<string, string>();
    columns.forEach((col, idx) => {
      colMap.set(col.id, idx);
      colNameMap.set(col.label.toUpperCase(), col.id);
    });
    this.columnMaps.set(sheetId, colMap);
    this.columnNameMaps.set(sheetId, colNameMap);
  }

  /**
   * Remove a sheet from the shared engine (cleanup when sheet is closed)
   * AC-6.3.7: Instance cleanup - unified architecture
   */
  public destroyEngine(sheetId: string): void {
    const hfNumericId = this.sheetNumericIds.get(sheetId);
    if (hfNumericId !== undefined && this.sharedEngine) {
      try {
        this.sharedEngine.removeSheet(hfNumericId);
      } catch {
        // Sheet may already be removed, ignore
      }
    }
    this.sheetNumericIds.delete(sheetId);
    this.columnMaps.delete(sheetId);
    this.columnNameMaps.delete(sheetId);
  }

  /**
   * Perform operations in batch mode for better performance
   * AC-6.3.9: Performance - batch mode for bulk operations
   */
  public batch<T>(sheetId: string, fn: () => T): T {
    const engine = this.getEngineForSheet(sheetId);
    engine.suspendEvaluation();
    try {
      const result = fn();
      return result;
    } finally {
      engine.resumeEvaluation();
    }
  }

  /**
   * Get performance metrics for the engine
   * AC-6.3.9: Performance logging
   */
  public getPerformanceMetrics(sheetId: string): {
    cellCount: number;
    sheetCount: number;
  } {
    const engine = this.getEngineForSheet(sheetId);
    const hfNumericId = this.sheetNumericIds.get(sheetId);
    if (hfNumericId === undefined) {
      return { cellCount: 0, sheetCount: 0 };
    }
    const dims = engine.getSheetDimensions(hfNumericId);
    return {
      cellCount: dims.width * dims.height,
      sheetCount: engine.countSheets(),
    };
  }

  // =============================================================================
  // PRIVATE HELPERS
  // =============================================================================

  /**
   * Convert 0-based column index to Excel column letter (A, B, ... Z, AA, AB, ...)
   */
  private indexToColumnLetter(index: number): string {
    let letter = '';
    let idx = index;
    while (idx >= 0) {
      letter = String.fromCharCode((idx % 26) + 65) + letter;
      idx = Math.floor(idx / 26) - 1;
    }
    return letter;
  }

  /**
   * Map HyperFormula error types to our error codes
   * AC-6.3.6: Error Handling
   */
  private mapHyperFormulaError(error: CellError | DetailedCellError): FormulaError {
    // DetailedCellError has .type as a string, CellError has .type as ErrorType
    const errorType = error.type;

    // Map ErrorType enum values
    if (errorType === ErrorType.REF) return { code: '#REF!', message: 'Invalid cell reference' };
    if (errorType === ErrorType.VALUE) return { code: '#VALUE!', message: 'Wrong value type' };
    if (errorType === ErrorType.DIV_BY_ZERO) return { code: '#DIV/0!', message: 'Division by zero' };
    if (errorType === ErrorType.NAME) return { code: '#NAME?', message: 'Unknown function or name' };
    if (errorType === ErrorType.NA) return { code: '#N/A', message: 'Value not available' };
    if (errorType === ErrorType.NUM) return { code: '#NUM!', message: 'Numeric error' };
    if (errorType === ErrorType.CYCLE) return { code: '#CYCLE!', message: 'Circular reference detected' };
    if (errorType === ErrorType.ERROR) return { code: '#ERROR!', message: 'General error' };
    if (errorType === ErrorType.SPILL) return { code: '#ERROR!', message: 'Spill error' };

    // Handle string type values (from DetailedCellError)
    const typeStr = String(errorType);
    if (typeStr === 'DIV_BY_ZERO') return { code: '#DIV/0!', message: 'Division by zero' };
    if (typeStr === 'NAME') return { code: '#NAME?', message: 'Unknown function or name' };
    if (typeStr === 'REF') return { code: '#REF!', message: 'Invalid cell reference' };
    if (typeStr === 'VALUE') return { code: '#VALUE!', message: 'Wrong value type' };
    if (typeStr === 'NA') return { code: '#N/A', message: 'Value not available' };
    if (typeStr === 'NUM') return { code: '#NUM!', message: 'Numeric error' };
    if (typeStr === 'NULL') return { code: '#NULL!', message: 'Null reference' };
    if (typeStr === 'CYCLE') return { code: '#CYCLE!', message: 'Circular reference detected' };
    if (typeStr === 'ERROR') return { code: '#ERROR!', message: 'General error' };

    return { code: '#ERROR!', message: 'Unknown error' };
  }
}

// =============================================================================
// SINGLETON INSTANCE
// AC-6.3.2: Singleton pattern for FormulaEngineManager
// =============================================================================

export const formulaEngineManager = new FormulaEngineManager();

// =============================================================================
// FEATURE FLAG
// AC-6.3.10: Feature flag for gradual rollout
// =============================================================================

export const USE_HYPERFORMULA = true;
